// Sound. Two independent buses — background music (one looping playlist) and sound
// effects (fired from the sim's `sfx` events) — each with its own 0–100 volume,
// remembered in localStorage and tunable live (`/set vol:bgm N`, `/set vol:sfx N`,
// or the lobby sliders).
//
// Everything is plain HTMLAudioElement: the files are short and few, and the browser
// already decodes, caches and mixes them. Browsers won't let a page make noise until
// the user has interacted, so nothing actually plays until `resume()` is called from
// a real gesture (the Join click) — before that, playSfx/startBgm quietly no-op.

const KEY = (bus) => `memewars.vol.${bus}`;
const clamp100 = (n) => Math.max(0, Math.min(100, Math.round(n)));
const enc = (path) => encodeURI("/" + path); // spaces -> %20; server decodes it back

function loadVol(bus, fallback) {
    try {
        const v = localStorage.getItem(KEY(bus));
        if (v != null && v !== "" && !Number.isNaN(+v)) return clamp100(+v);
    } catch { /* private mode: fall through to the default */ }
    return clamp100(fallback);
}
function saveVol(bus, n) { try { localStorage.setItem(KEY(bus), String(n)); } catch { /* ignore */ } }

export function createAudio(config) {
    const vol = {
        bgm: loadVol("bgm", config.bgm?.volume ?? 40),
        sfx: loadVol("sfx", config.sfx?.volume ?? 70),
    };

    // --- sfx: a small pool per sound so rapid or overlapping shots don't cut each
    //     other off, plus a per-sound throttle so a rapidfire weapon doesn't stack
    //     twenty identical hits into a wall of noise.
    const POOL = 4, THROTTLE_MS = 35;
    // "Sustained" sounds (e.g. the cereal crunch) are long samples tied to a held
    // trigger: a single looping instance that plays while the shots keep coming and
    // stops shortly after they stop, rather than a fresh multi-second copy per shot
    // that keeps crunching long after you let go. HOLD must outlast the gap between
    // bursts of a rapidfire weapon so the loop doesn't stutter mid-hold.
    const SUSTAIN_HOLD_MS = 700;
    const sustained = new Set(config.sfx?.sustained || []);
    // per-sound loudness trim (0..1), e.g. shoop is deafening. A mutable copy of the
    // data-file defaults so `/set gain:<sound> N` can tune it live for the session
    // (reload reverts to data/audio.json, which stays the source of truth).
    const gain = { ...(config.sfx?.gain || {}) };
    // Effective element volume for a sound: master sfx bus × its own trim × distance
    // attenuation (1 at the source, fading to 0 far away — passed in by the caller,
    // which knows where the listener is).
    const effVol = (name, atten) => Math.max(0, Math.min(1, (vol.sfx / 100) * (gain[name] ?? 1) * atten));
    const pools = {}, cursor = {}, lastAt = {};
    const holdInst = {}, holdUntil = {};
    for (const [name, path] of Object.entries(config.sfx?.map || {})) {
        if (sustained.has(name)) {
            const a = new Audio(enc(path));
            a.preload = "auto"; a.loop = true;
            holdInst[name] = a; holdUntil[name] = 0;
            continue;
        }
        pools[name] = Array.from({ length: POOL }, () => {
            const a = new Audio(enc(path));
            a.preload = "auto";
            return a;
        });
        cursor[name] = 0; lastAt[name] = -1e9;
    }

    // While any sustained sound is live, poll for ones whose trigger has gone quiet
    // and stop them. The timer only runs when something is sustaining.
    let holdTimer = null;
    function ensureHoldTimer() {
        if (holdTimer != null) return;
        holdTimer = setInterval(() => {
            let anyLive = false;
            const now = performance.now();
            for (const [name, a] of Object.entries(holdInst)) {
                if (a.paused) continue;
                if (now >= holdUntil[name]) { a.pause(); try { a.currentTime = 0; } catch { /* ignore */ } }
                else anyLive = true;
            }
            if (!anyLive) { clearInterval(holdTimer); holdTimer = null; }
        }, 90);
    }

    // --- bgm: the server DJs (syncMusic); local rotation is the fallback -----------
    // Tracks are {src, seconds}; a bare string still works. `seconds` lets syncMusic seek
    // to the shared position, and drives the local fallback rotation.
    const trackList = config.bgm?.tracks || [];
    const tracks = trackList.map((t) => enc(typeof t === "string" ? t : t.src));
    const trackSecs = trackList.map((t) => (typeof t === "object" && t.seconds) || 0);
    let bgm = null, trackIdx = 0, resumed = false, wantBgm = false, serverDriven = false;

    function ensureBgm() {
        if (!bgm) {
            bgm = new Audio();
            // Auto-advance is the OFFLINE fallback; once the server is DJing (syncMusic) it
            // owns the switch, and we loop the current track so nothing stops between cues.
            bgm.addEventListener("ended", () => { if (serverDriven) return; trackIdx = (trackIdx + 1) % tracks.length; playTrack(); });
        }
        return bgm;
    }
    function playTrack() {
        if (!tracks.length) return;
        ensureBgm();
        bgm.src = tracks[trackIdx];
        bgm.volume = vol.bgm / 100;
        bgm.play().catch(() => { /* blocked until a gesture; resume() retries */ });
    }

    // --- jetpack synth: filtered white-noise roar + computery blips, no sample ----
    const jet = (() => {
        const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
        let ctx = null, src = null, gainNode = null, on = false, until = 0, timer = null, lastBeep = 0;
        function ensure() {
            if (!AC) return null;
            if (!ctx) ctx = new AC();
            if (ctx.state === "suspended") ctx.resume();
            return ctx;
        }
        function start(c) {
            const buf = c.createBuffer(1, c.sampleRate, c.sampleRate); // 1s of noise, looped
            const d = buf.getChannelData(0);
            for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
            src = c.createBufferSource(); src.buffer = buf; src.loop = true;
            const band = c.createBiquadFilter(); band.type = "bandpass"; band.frequency.value = 820; band.Q.value = 0.6;
            gainNode = c.createGain(); gainNode.gain.value = 0;
            src.connect(band); band.connect(gainNode); gainNode.connect(c.destination);
            src.start();
            on = true;
        }
        function beep(c, level) {
            const o = c.createOscillator(), g = c.createGain();
            o.type = "square";
            o.frequency.value = 1000 + Math.floor(Math.random() * 5) * 220;
            o.connect(g); g.connect(c.destination);
            const t = c.currentTime;
            g.gain.setValueAtTime(0, t);
            g.gain.linearRampToValueAtTime(level * 0.1, t + 0.008);
            g.gain.linearRampToValueAtTime(0, t + 0.09);
            o.start(t); o.stop(t + 0.1);
        }
        function ensureTimer() {
            if (timer != null) return;
            timer = setInterval(() => {
                if (on && performance.now() >= until) {
                    if (gainNode) gainNode.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
                    on = false;
                    const s = src; src = null;
                    setTimeout(() => { try { s && s.stop(); } catch { /* already stopped */ } }, 250);
                }
                if (!on) { clearInterval(timer); timer = null; }
            }, 80);
        }
        return {
            unlock() { ensure(); },
            thrust(level) {
                const c = ensure(); if (!c) return;
                if (!on) start(c);
                gainNode.gain.setTargetAtTime(level * 0.5, c.currentTime, 0.03); // roar level
                until = performance.now() + 200; // bridge the gap between thrust ticks
                const now = performance.now();
                if (now - lastBeep > 380) { lastBeep = now; beep(c, level); }
                ensureTimer();
            },
        };
    })();

    return {
        // Call from a user gesture (the Join click). Unlocks playback and kicks off
        // the music; safe to call more than once.
        resume() {
            resumed = true;
            jet.unlock();
            if (wantBgm && (!bgm || bgm.paused)) playTrack();
        },
        startBgm() {
            wantBgm = true;
            if (resumed) playTrack();
        },
        stopBgm() { wantBgm = false; if (bgm) bgm.pause(); },

        // Follow the server's shared playlist: play track `i`, seeking to where the timeline
        // is right now — (server clock − track start), the server clock estimated from
        // `serverOffset` (ping-derived). Re-called on every music cue and every ping, so the
        // position self-corrects; we only re-seek on a real drift to avoid stutter.
        syncMusic(i, startedAt, serverOffset = 0) {
            if (!tracks.length || !wantBgm || i < 0 || i >= tracks.length) return;
            serverDriven = true;
            ensureBgm();
            bgm.loop = true;
            bgm.volume = vol.bgm / 100;
            let pos = (Date.now() + serverOffset - startedAt) / 1000;
            const dur = trackSecs[i];
            if (dur > 0) pos = ((pos % dur) + dur) % dur; // wrap in case we're a hair past the end
            if (trackIdx !== i) { trackIdx = i; bgm.src = tracks[i]; }
            const seek = () => { try { if (Math.abs((bgm.currentTime || 0) - pos) > 1.5) bgm.currentTime = pos; } catch { /* not seekable yet */ } };
            if (bgm.readyState >= 1) seek(); else bgm.addEventListener("loadedmetadata", seek, { once: true });
            bgm.play().catch(() => { /* blocked until a gesture */ });
        },

        // atten (0..1) is distance attenuation from the listener; 1 for your own shots.
        playSfx(name, atten = 1) {
            if (!resumed || vol.sfx <= 0 || atten <= 0) return;
            const v = effVol(name, atten);
            if (v <= 0) return;
            // Sustained sound: (re)start the single looping instance and push out the
            // deadline; the hold timer stops it once the shots stop arriving.
            const held = holdInst[name];
            if (held) {
                held.volume = v;
                if (held.paused) { try { held.currentTime = 0; } catch { /* ignore */ } held.play().catch(() => {}); }
                holdUntil[name] = performance.now() + SUSTAIN_HOLD_MS;
                ensureHoldTimer();
                return;
            }
            const pool = pools[name];
            if (!pool) return; // a sound with no file mapped — silently nothing
            const now = performance.now();
            if (now - lastAt[name] < THROTTLE_MS) return;
            lastAt[name] = now;
            const a = pool[cursor[name] = (cursor[name] + 1) % pool.length];
            a.volume = v;
            try { a.currentTime = 0; } catch { /* not seekable yet */ }
            a.play().catch(() => { /* still locked, or overlapping — ignore */ });
        },

        // The jetpack: a synthesized bandpass-filtered white-noise roar (WebAudio, no
        // file) that stays on while thrust events arrive and fades out when they stop,
        // punctuated by occasional computery blips. Driven by the sim's `thrust` events.
        jetThrust(atten = 1) {
            if (!resumed || vol.sfx <= 0 || atten <= 0) return;
            jet.thrust(effVol("jet", atten));
        },

        setVol(bus, n) {
            if (bus !== "bgm" && bus !== "sfx") return null;
            vol[bus] = clamp100(n);
            saveVol(bus, vol[bus]);
            if (bus === "bgm" && bgm) bgm.volume = vol.bgm / 100;
            if (bus === "sfx") for (const a of Object.values(holdInst)) if (!a.paused) a.volume = vol.sfx / 100;
            return vol[bus];
        },
        getVol(bus) { return vol[bus]; },

        // Live per-sound trim, for tuning by ear before baking the value into
        // data/audio.json. Session-only (not persisted). Returns the current value.
        setGain(name, v) {
            const g = Math.max(0, Math.min(4, +v));
            if (Number.isFinite(g)) gain[name] = g;
            return gain[name] ?? 1;
        },
        getGain(name) { return gain[name] ?? 1; },
    };
}
